import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Snackbar,
  styled
} from '@mui/material';
import { fetchOrganizations, UserNotActivatedError } from '../../services/orgService';
import { transferDesigns, copyDesigns, TransferResponse, CopyResponse } from '../../services/designsService';
import LocationDropdown from '../molecules/LocationDropdown';
import DialogTitle from '../atoms/DialogTitle';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  onTransfer?: (organizationId: string, locationId: string) => void;
  onShare?: (organizationId: string, locationId: string) => void;
  selectedDesignIds?: string[];
  onSuccess?: (message: string, type: 'transfer' | 'share') => void;
  onError?: (error: string, type: 'transfer' | 'share') => void;
}

interface Organization {
  id: string;
  name: string;
}


const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  onTransfer,
  onShare,
  selectedDesignIds = [],
  onSuccess,
  onError
}) => {
  const selectedCount = selectedDesignIds.length;
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
  const [showAlert, setShowAlert] = useState(false);

  // Load organizations when dialog opens
  useEffect(() => {
    if (open) {
      // Reset selections when dialog opens
      setSelectedOrganization('');
      setSelectedLocation('');
      loadOrganizations();
    }
  }, [open]);

  const loadOrganizations = async () => {
    setLoadingOrganizations(true);
    try {
      const orgs = await fetchOrganizations();
      // Include all organizations (including current one for sharing within same org)
      setOrganizations(orgs);

      // Automatically select the first organization if available
      if (orgs.length > 0) {
        setSelectedOrganization(orgs[0].id);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);

      // Handle specific error types
      if (error instanceof UserNotActivatedError) {
        setAlertMessage('Your account is not activated in this region. Please contact your administrator.');
        setAlertSeverity('error');
        setShowAlert(true);
      } else {
        setAlertMessage('Unable to retrieve organizations. Please try again later.');
        setAlertSeverity('error');
        setShowAlert(true);
      }
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // Reset location when organization changes
  useEffect(() => {
    if (!selectedOrganization) {
      setSelectedLocation('');
    }
  }, [selectedOrganization]);

  const handleOrganizationChange = (event: SelectChangeEvent) => {
    setSelectedOrganization(event.target.value);
    setSelectedLocation(''); // Reset location when organization changes
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocation(locationId);
  };

  const handleTransfer = async () => {
    if (selectedOrganization) {
      // If no design IDs provided, just call the legacy callback
      if (!selectedDesignIds || selectedDesignIds.length === 0) {
        if (onTransfer) {
          onTransfer(selectedOrganization, selectedLocation || '');
        }
        handleClose();
        return;
      }
      
      setIsProcessing(true);
      try {
        const response = await transferDesigns(
          selectedDesignIds,
          selectedOrganization,
          selectedLocation || undefined
        );
        
        if (response.success) {
          const successCount = response.results.filter(r => r.success).length;
          const failCount = response.results.filter(r => !r.success).length;
          
          let message = `Successfully transferred ${successCount} design${successCount !== 1 ? 's' : ''}`;
          if (failCount > 0) {
            message += ` (${failCount} failed)`;
          }
          
          // Show success message
          setAlertMessage(message);
          setAlertSeverity('success');
          setShowAlert(true);
          
          if (onSuccess) {
            onSuccess(message, 'transfer');
          }
          
          // Call legacy callback if provided
          if (onTransfer) {
            onTransfer(selectedOrganization, selectedLocation || '');
          }
          
          // Close dialog after a short delay to show the success message
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          throw new Error(response.message || 'Transfer failed');
        }
      } catch (error) {
        console.error('Transfer error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to transfer designs';
        
        // Show error message
        setAlertMessage(errorMessage);
        setAlertSeverity('error');
        setShowAlert(true);
        
        if (onError) {
          onError(errorMessage, 'transfer');
        }
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleShare = async () => {
    if (selectedOrganization) {
      // If no design IDs provided, just call the legacy callback
      if (!selectedDesignIds || selectedDesignIds.length === 0) {
        if (onShare) {
          onShare(selectedOrganization, selectedLocation || '');
        }
        handleClose();
        return;
      }
      
      setIsProcessing(true);
      try {
        const response = await copyDesigns(
          selectedDesignIds,
          selectedOrganization,
          selectedLocation || ''
        );
        
        if (response.success) {
          const successCount = response.results.filter(r => r.success).length;
          const failCount = response.results.filter(r => !r.success).length;
          
          let message = `Successfully shared ${successCount} design${successCount !== 1 ? 's' : ''}`;
          if (failCount > 0) {
            message += ` (${failCount} failed)`;
          }
          
          // Show success message
          setAlertMessage(message);
          setAlertSeverity('success');
          setShowAlert(true);
          
          if (onSuccess) {
            onSuccess(message, 'share');
          }
          
          // Call legacy callback if provided
          if (onShare) {
            onShare(selectedOrganization, selectedLocation || '');
          }
          
          // Close dialog after a short delay to show the success message
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          throw new Error(response.message || 'Share failed');
        }
      } catch (error) {
        console.error('Share error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to share designs';
        
        // Show error message
        setAlertMessage(errorMessage);
        setAlertSeverity('error');
        setShowAlert(true);
        
        if (onError) {
          onError(errorMessage, 'share');
        }
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    // Reset selections and alert state when closing
    setSelectedOrganization('');
    setSelectedLocation('');
    setShowAlert(false);
    setAlertMessage('');
    onClose();
  };

  // Get current organization ID from session storage
  const currentOrgId = sessionStorage.getItem('selectedOrgId');
  const isCurrentOrg = selectedOrganization === currentOrgId;
  
  // Valid for share: organization must be selected
  // Valid for transfer: organization must be selected AND different from current org
  const isValidForShare = selectedOrganization;
  const isValidForTransfer = selectedOrganization && !isCurrentOrg;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only close on escape key or close button click, not backdrop click
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      onKeyDown={handleKeyDown}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      slotProps={{
        paper: {
          sx: {
            border: theme => `0.6px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            minWidth: '400px',
          },
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)', // For Safari support
        }
      }}
    >
      <DialogTitle onClose={handleClose}>
        Share Designs
      </DialogTitle>

      <StyledDialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 15, pt: 1 }}>
          <Box>
            <Typography sx={{ margin: 0, color: theme => theme.palette.text.primary, fontSize: '14px', lineHeight: '1.4' }}>
              Share your {selectedCount} design{selectedCount !== 1 ? 's' : ''} with other organizations and locations. Choose to transfer ownership or share a copy.
            </Typography>
            <Typography sx={{ mt: 1, margin: '8px 0 0 0', color: theme => theme.palette.text.secondary, fontSize: '13px', lineHeight: '1.4' }}>
              If the destination organization is not listed, you can export your design files and import them there.
            </Typography>
          </Box>
          <Box sx={{ height: '10px' }} />
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                margin: '0 0 5px 0',
                color: theme => theme.palette.text.primary, 
                fontWeight: 600,
                fontSize: '12px'
              }}
            >
              Organization
            </Typography>
            <StyledFormControl fullWidth disabled={loadingOrganizations}>
              <Select
                id="organization-select"
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                displayEmpty
                endAdornment={
                  loadingOrganizations && (
                    <CircularProgress size={20} sx={{ position: 'absolute', right: 30 }} />
                  )
                }
              >
                {organizations.length === 0 && !loadingOrganizations ? (
                  <MenuItem value="" disabled sx={{ fontFamily: 'Open Sans', fontSize: '14px' }}>
                    No organizations available
                  </MenuItem>
                ) : (
                  organizations.map((org) => {
                    const currentOrgId = sessionStorage.getItem('selectedOrgId');
                    const isCurrentOrg = org.id === currentOrgId;
                    return (
                      <MenuItem key={org.id} value={org.id} sx={{ fontFamily: 'Open Sans', fontSize: '14px' }}>
                        {org.name}{isCurrentOrg ? ' (Current)' : ''}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </StyledFormControl>
          </Box>
          <Box sx={{ height: '10px' }} />
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                margin: '0 0 5px 0',
                color: theme => theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '12px'
              }}
            >
              Workplace location
            </Typography>
            <LocationDropdown
              organizationId={selectedOrganization}
              value={selectedLocation}
              onChange={handleLocationChange}
              disabled={!selectedOrganization}
              placeholder="No location selected (optional)"
            />
          </Box>
        </Box>
        
        <ButtonContainer>
          <ActionButton onClick={handleClose} buttonVariant="cancel" disabled={isProcessing}>
            Cancel
          </ActionButton>
          <ActionButton
            onClick={handleTransfer}
            disabled={!isValidForTransfer || isProcessing}
            buttonVariant="cancel"
            title={isCurrentOrg ? "Cannot transfer to the same organization" : "Transfer ownership"}
          >
            {isProcessing ? (
              <CircularProgress size={16} sx={{ color: theme => theme.palette.text.primary }} />
            ) : (
              <Box
                component="img"
                src="/images/Transfer.svg"
                alt="Transfer"
                sx={{
                  width: '16px',
                  height: '16px',
                  filter: theme => theme.palette.mode === 'dark'
                    ? 'brightness(0) saturate(100%) invert(1)'
                    : 'brightness(0) saturate(100%)'
                }}
              />
            )}
            Transfer
          </ActionButton>
          <ActionButton 
            onClick={handleShare}
            disabled={!isValidForShare || isProcessing}
            buttonVariant="primary"
          >
            {isProcessing ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <img 
                src="/images/Share.svg" 
                alt="Share" 
                style={{ width: '16px', height: '16px', filter: 'brightness(0) invert(1)' }}
              />
            )}
            Share
          </ActionButton>
        </ButtonContainer>
      </StyledDialogContent>
      
      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowAlert(false)} 
          severity={alertSeverity} 
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

// Styled Components matching Save dialog style
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: '0 24px 16px 24px',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    height: '40px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      border: `1px solid ${theme.palette.action.hover}`,
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      '& .MuiSelect-select': {
        color: theme.palette.text.disabled,
      },
    },
  },
  '& .MuiSelect-select': {
    backgroundColor: 'transparent',
    height: '40px',
    lineHeight: '40px',
    padding: '0 32px 0 14px',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Open Sans',
    fontSize: '14px',
    color: theme.palette.text.primary,
  },
  '& .MuiSelect-icon': {
    right: '14px',
    color: theme.palette.text.secondary,
  },
  '& .MuiMenuItem-root': {
    fontFamily: 'Open Sans',
    fontSize: '14px',
    color: theme.palette.text.primary,
  },
}));

const ButtonContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
  justifyContent: 'flex-end',
});

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'buttonVariant'
})<{ buttonVariant?: 'cancel' | 'primary' }>(({ theme, buttonVariant }) => ({
  height: '40px',
  padding: '0 24px',
  borderRadius: '6px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  minWidth: '100px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  border: buttonVariant === 'cancel' ? `1px solid ${theme.palette.divider}` : 'none',
  backgroundColor: buttonVariant === 'cancel' ? theme.palette.background.paper : theme.palette.primary.main,
  color: buttonVariant === 'cancel' ? theme.palette.text.primary : theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: buttonVariant === 'cancel' ? theme.palette.action.hover : theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    border: buttonVariant === 'cancel' ? `1px solid ${theme.palette.divider}` : 'none',
    '& img': {
      opacity: 0.5,
    },
  },
}));

export default ShareDialog;